import { Platform } from 'react-native'
import { Env } from '../../../env'

export enum PaymentType {
  PAYPAL = 'paypal',
  WECHAT = 'wechat',
  DEV = 'dev',
  ALIPAY = 'alipay',
  ANTOM_CARD = 'antom-card',
  ANTOM_ALIPAY = 'antom-alipay',
}

export enum PaymentConnectionType {
  PAYPAL = 'paypal',
  WECHAT = 'wechat',
  ANTOM_CARD = 'antom-card',
  ANTOM_ALIPAY = 'antom-alipay',
}

export enum PaymentEnvironment {
  DEVELOP = 'develop',
  STAGING = 'staging',
  STAGING_CN = 'staging-cn',
  GLOBAL = 'global',
  CN = 'cn',
}

type PaymentSurface = 'channel' | 'connection'

type PaymentVisibilityRule = {
  type: PaymentType | PaymentConnectionType
  surface: PaymentSurface
  environments: PaymentEnvironment[]
}

const PAYMENT_VISIBILITY_RULES: PaymentVisibilityRule[] = [
  {
    type: PaymentType.PAYPAL,
    surface: 'channel',
    environments: [
      PaymentEnvironment.DEVELOP,
      PaymentEnvironment.STAGING,
      PaymentEnvironment.STAGING_CN,
    ],
  },
  {
    type: PaymentType.WECHAT,
    surface: 'channel',
    environments: [
      PaymentEnvironment.DEVELOP,
      PaymentEnvironment.STAGING,
      PaymentEnvironment.STAGING_CN,
      PaymentEnvironment.CN,
    ],
  },
  {
    type: PaymentType.DEV,
    surface: 'channel',
    environments: [
      PaymentEnvironment.DEVELOP,
      PaymentEnvironment.STAGING,
      PaymentEnvironment.STAGING_CN,
    ],
  },
  {
    type: PaymentType.ALIPAY,
    surface: 'channel',
    environments: [],
  },
  {
    type: PaymentType.ANTOM_CARD,
    surface: 'channel',
    environments: [],
  },
  {
    type: PaymentType.ANTOM_ALIPAY,
    surface: 'channel',
    environments: [],
  },
  {
    type: PaymentConnectionType.PAYPAL,
    surface: 'connection',
    environments: [
      PaymentEnvironment.DEVELOP,
      PaymentEnvironment.STAGING,
      PaymentEnvironment.STAGING_CN,
    ],
  },
  {
    type: PaymentConnectionType.WECHAT,
    surface: 'connection',
    environments: [PaymentEnvironment.DEVELOP, PaymentEnvironment.STAGING],
  },
]

const getActivePaymentEnvironments = () => {
  const envs = new Set<PaymentEnvironment>()

  if (Env.IS_DEVELOP) {
    envs.add(PaymentEnvironment.DEVELOP)
  }

  if (Env.IS_STAGE && Env.IS_CN) {
    envs.add(PaymentEnvironment.STAGING_CN)
  }

  if (Env.IS_STAGE && !Env.IS_CN) {
    envs.add(PaymentEnvironment.STAGING)
  }

  if (Env.IS_PRODUCTION && Env.IS_CN) {
    envs.add(PaymentEnvironment.CN)
  }

  if (Env.IS_PRODUCTION && !Env.IS_CN) {
    envs.add(PaymentEnvironment.GLOBAL)
  }

  return Array.from(envs)
}

const isPaymentEnabled = (
  type: PaymentType | PaymentConnectionType,
  surface: PaymentSurface
) => {
  const rule = PAYMENT_VISIBILITY_RULES.find(
    (item) => item.type === type && item.surface === surface
  )
  if (!rule) {
    return false
  }

  const activeEnvironments = getActivePaymentEnvironments()
  return activeEnvironments.some((env) => rule.environments.includes(env))
}

export const isPaymentChannelEnabled = (type: PaymentType) => {
  if (Platform.OS !== 'web') {
    return false
  }

  if (type === PaymentType.PAYPAL) {
    return Boolean(Env.PAYPAL_CLIENT_ID) && isPaymentEnabled(type, 'channel')
  }

  return isPaymentEnabled(type, 'channel')
}

export const isPaymentConnectionEnabled = (type: PaymentConnectionType) => {
  if (type === PaymentConnectionType.PAYPAL) {
    return Boolean(Env.PAYPAL_CLIENT_ID) && isPaymentEnabled(type, 'connection')
  }

  return isPaymentEnabled(type, 'connection')
}
